import React, { useState, useEffect, useRef } from "react";
import { VehiclesClient } from "../web-api-client.ts";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Button, Modal, InputGroup, For, Card } from "react-bootstrap";
import NewVehicle from "./NewVehicle.js";
import BackButton from "./BackButton.js";
import { isMobileDevice } from "./utils/utils.js";
import ClearableInput from "./ClearableInput.js";
import ScannerLauncher from "./ScannerLauncher.js";
import Loader from "./Loader.js";
import UserContext from "../providers/UserContext.js";

const Vehicles = ({ status: initialFilterStatus }) => {
  const { barcode } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const user = React.useContext(UserContext);

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");

  // NOTE: leaving these in place since they exist in your component signature/state,
  // but they're not used in the current UI logic. Keeping them avoids breaking callers.
  const [filterStatus, setFilterStatus] = useState(initialFilterStatus || false);

  const [sortColumn, setSortColumn] = useState("created");
  const [sortDirection, setSortDirection] = useState("desc");

  const [makes, setMakes] = useState([]);

  const [filtersReady, setFiltersReady] = useState(false);
  const [filters, setFilters] = useState({
    make: null,  // expect MakeId (number) or null
    model: null, // expect ModelId (number) or null
    year: null,  // expect number or null
    status: null, // expect number (0/1/...) or null
  });

  const itemsPerPage = 15;

  const hasLoadedRef = useRef(false);
  const reqIdRef = useRef(0);

  // Utility to build query string (be careful with 0 values)
  const buildQuery = () => {
    const params = new URLSearchParams();

    if (searchTerm) params.set("search", searchTerm);

    if (filters.make !== null && filters.make !== undefined && filters.make !== "") {
      params.set("make", String(filters.make));
    }
    if (filters.model !== null && filters.model !== undefined && filters.model !== "") {
      params.set("model", String(filters.model));
    }
    if (filters.year !== null && filters.year !== undefined && filters.year !== "") {
      params.set("year", String(filters.year));
    }
    if (filters.status !== null && filters.status !== undefined && filters.status !== "") {
      params.set("status", String(filters.status));
    }

    params.set("sort", sortColumn);
    params.set("dir", sortDirection);
    params.set("page", String(currentPage));

    return `?${params.toString()}`;
  };

  const handleRowClick = (vehicleId) => {
    navigate(`/vehicles/${vehicleId}${buildQuery()}`);
  };

  const populateVehicleData = async (page, state) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);

    const client = new VehiclesClient();

    const {
      searchTerm: s,
      sortColumn: sc,
      sortDirection: sd,
      filters: f
    } = state ?? { searchTerm, sortColumn, sortDirection, filters };

    try {
      const data = await client.getVehicleList(
        user.dealerId,
        page,
        itemsPerPage,
        s,
        sc,
        sd,
        f?.make ?? null,
        f?.model ?? null,
        f?.year ?? null,
        f?.status ?? null,
      );

      // Ignore stale response
      if (reqId !== reqIdRef.current) return;

      setVehicles(data.items);
      setTotalPages(data.totalPages);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  // Fetch makes once (and show Add Vehicle modal if barcode param exists)
  useEffect(() => {
    if (barcode) setShowAddVehicleModal(true);

    const fetchMakes = async () => {
      const client = new VehiclesClient();
      const makeData = await client.getMakes(null);
      setMakes(makeData);
    };

    fetchMakes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial hydration from query string (single source of truth)
  useEffect(() => {
    const fetchInitialData = async () => {
      const initialSearch = searchParams.get("search") || "";

      const makeStr = searchParams.get("make");
      const modelStr = searchParams.get("model");
      const yearStr = searchParams.get("year");
      const statusStr = searchParams.get("status");

      // IMPORTANT: do NOT default status to 0 when param missing
      const initialMake =
        makeStr !== null && makeStr !== "" && !Number.isNaN(Number(makeStr))
          ? Number(makeStr)
          : null;

      const initialModel =
        modelStr !== null && modelStr !== "" && !Number.isNaN(Number(modelStr))
          ? Number(modelStr)
          : null;

      const initialYear =
        yearStr !== null && yearStr !== "" && !Number.isNaN(Number(yearStr))
          ? Number(yearStr)
          : null;

      const initialStatus =
        statusStr !== null && statusStr !== "" && !Number.isNaN(Number(statusStr))
          ? Number(statusStr)
          : null;

      const initialSort = searchParams.get("sort") || "make";
      const initialDir = searchParams.get("dir") || "asc";
      const initialPage = parseInt(searchParams.get("page"), 10) || 1;

      setSearchTerm(initialSearch);
      setFilters({
        make: initialMake,
        model: initialModel,
        year: initialYear,
        status: initialStatus
      });
      setSortColumn(initialSort);
      setSortDirection(initialDir);
      setCurrentPage(initialPage);

      setFiltersReady(true);
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once filters ready, load data once, then allow URL sync + param watching
  useEffect(() => {
    if (!filtersReady) return;

    populateVehicleData(currentPage, { searchTerm, sortColumn, sortDirection, filters });
    hasLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady]);

  // Watch URL param changes (back/forward nav, manual edits)
  useEffect(() => {
    if (!hasLoadedRef.current) return;

    const newSearch = searchParams.get("search") || "";

    const makeStr = searchParams.get("make");
    const modelStr = searchParams.get("model");
    const yearStr = searchParams.get("year");
    const statusStr = searchParams.get("status");

    const newMake =
      makeStr !== null && makeStr !== "" && !Number.isNaN(Number(makeStr))
        ? Number(makeStr)
        : null;

    const newModel =
      modelStr !== null && modelStr !== "" && !Number.isNaN(Number(modelStr))
        ? Number(modelStr)
        : null;

    const newYear =
      yearStr !== null && yearStr !== "" && !Number.isNaN(Number(yearStr))
        ? Number(yearStr)
        : null;

    const newStatus =
      statusStr !== null && statusStr !== "" && !Number.isNaN(Number(statusStr))
        ? Number(statusStr)
        : null;

    const newSort = searchParams.get("sort") || "created";
    const newDir = searchParams.get("dir") || "desc";
    const newPage = parseInt(searchParams.get("page"), 10) || 1;

    setSearchTerm(newSearch);
    setFilters({ make: newMake, model: newModel, year: newYear, status: newStatus });
    setSortColumn(newSort);
    setSortDirection(newDir);
    setCurrentPage(newPage);
  }, [searchParams]);

  // Single fetch effect (no other direct fetching)
  useEffect(() => {
    if (!hasLoadedRef.current) return;

    populateVehicleData(currentPage, { searchTerm, sortColumn, sortDirection, filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, sortColumn, sortDirection, currentPage]);

  // Keep URL in sync with state (after initial hydration)
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    navigate(buildQuery(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, sortColumn, sortDirection, currentPage]);

  const resetList = async () => {
    // Don’t fetch directly; reset state and let the single fetch effect run
    setSearchTerm("");
    setFilters({ make: null, model: null, year: null, status: null });
    setSortColumn("created");
    setSortDirection("desc");
    setCurrentPage(1);
  };

  const handleAddVehicle = () => {
    setShowAddVehicleModal(true);
  };

  const closeAddVehicleModal = () => {
    setShowAddVehicleModal(false);
    // refresh list via single fetch path
    // keep current filters/page; just trigger fetch explicitly to pick up newly created vehicle
    populateVehicleData(currentPage, { searchTerm, sortColumn, sortDirection, filters });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleScan = (scannedBarcode) => {
    setSearchTerm(scannedBarcode);
    setCurrentPage(1);
  };

  const handleSearch = async (eOrString) => {
    // Enter/search triggers should not fetch directly; just adjust state
    const value =
      typeof eOrString === "string"
        ? eOrString
        : eOrString?.target?.value ?? searchTerm;

    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // ===== Mobile Card Renderer =====
  const renderMobileCard = (v) => (
    <Card
      key={v.id}
      className="mb-4 mt-4 shadow-sm"
      role="button"
      onClick={() => handleRowClick(v.id)}
      style={{ transition: 'transform 0.2s' }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <Card.Body className="p-3">
        {/* Vehicle Info - Main Header */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="flex-grow-1">
            <h6 className="mb-1 fw-bold">
              {v.year} {v.makeName} {v.modelName}
            </h6>
            <div className="text-muted">{v.color}</div>
          </div>
        </div>

        {/* Divider */}
        <hr className="my-1" style={{ opacity: 0.1 }} />

        {/* Details Section */}
        <div className="small">
          <div className="d-flex align-items-center mb-2 text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="me-2" viewBox="0 0 16 16">
              <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
            </svg>
            Stock #: <span className="ms-1">{v.stockNumber || 'N/A'}</span>
          </div>

          <div className="d-flex align-items-center mb-2 text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="me-2" viewBox="0 0 16 16">
              <path d="M12.643 15C13.979 15 15 13.845 15 12.5V5H1v7.5C1 13.845 2.021 15 3.357 15h9.286zM5.5 7h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1zM.8 1a.8.8 0 0 0-.8.8V3a.8.8 0 0 0 .8.8h14.4A.8.8 0 0 0 16 3V1.8a.8.8 0 0 0-.8-.8H.8z" />
            </svg>
            VIN: <span className="ms-1" style={{ fontSize: '1em' }}>{v.vin || 'N/A'}</span>
          </div>

          <div className="d-flex align-items-center text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="me-2" viewBox="0 0 16 16">
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1zm1-1h12V3a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v.5-.5z" />
              <rect x="3" y="6" width="2" height="2" rx="0.3" />
              <rect x="7" y="6" width="2" height="2" rx="0.3" />
              <rect x="11" y="6" width="2" height="2" rx="0.3" />
              <rect x="3" y="9.5" width="2" height="2" rx="0.3" />
              <rect x="7" y="9.5" width="2" height="2" rx="0.3" />
              <rect x="11" y="9.5" width="2" height="2" rx="0.3" />
            </svg>
            Date Added:
            <span className="ms-1" style={{ fontSize: '1em' }}>
              {v.created
                ? new Date(v.created).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                + ', '
                + new Date(v.created).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                : 'N/A'}
            </span>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <div className="container mb-5" style={{ marginBottom: "90px!important" }}>
      <BackButton />
      <h1>Vehicles</h1>

      <div className="row g-2 align-items-center">
        <div className="col-12 col-md-8 d-flex flex-wrap align-items-center gap-3">
          <InputGroup style={{ maxWidth: "300px" }}>
            <ClearableInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onEnter={handleSearch}
              onClear={resetList}
            />
            <ScannerLauncher
              onBarcodeDetected={handleScan}
              mode="button"
              buttonClasses="ms-2"
            />
          </InputGroup>
        </div>

        <div className="col-12 col-md-4 d-flex justify-content-md-end">
          <button
            className="btn btn-primary w-100 w-md-auto"
            onClick={handleAddVehicle}
          >
            Add Vehicle
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div>
          <div className="d-none d-md-block">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th onClick={() => handleSort("stockNumber")} style={{ cursor: "pointer" }}>
                    Stock Number{" "}
                    {sortColumn === "stockNumber" && (sortDirection === "asc" ? "▲" : "▼")}
                  </th>

                  <th onClick={() => handleSort("year")} style={{ cursor: "pointer" }}>
                    Year {sortColumn === "year" && (sortDirection === "asc" ? "▲" : "▼")}
                  </th>

                  <th onClick={() => handleSort("make")} style={{ cursor: "pointer" }}>
                    Make {sortColumn === "make" && (sortDirection === "asc" ? "▲" : "▼")}
                  </th>

                  <th onClick={() => handleSort("model")} style={{ cursor: "pointer" }}>
                    Model {sortColumn === "model" && (sortDirection === "asc" ? "▲" : "▼")}
                  </th>

                  <th onClick={() => handleSort("trim")} style={{ cursor: "pointer" }}>
                    Trim {sortColumn === "trim" && (sortDirection === "asc" ? "▲" : "▼")}
                  </th>

                  <th onClick={() => handleSort("color")} style={{ cursor: "pointer" }}>
                    Color {sortColumn === "color" && (sortDirection === "asc" ? "▲" : "▼")}
                  </th>

                  {!isMobileDevice() && (
                    <th onClick={() => handleSort("vin")} style={{ cursor: "pointer" }}>
                      Vin {sortColumn === "vin" && (sortDirection === "asc" ? "▲" : "▼")}
                    </th>
                  )}

                  <th onClick={() => handleSort("created")} style={{ cursor: "pointer" }}>
                    Date Added {sortColumn === "created" && (sortDirection === "asc" ? "▲" : "▼")}
                  </th>


                </tr>

                {/* Filter Row */}
                {/*  <tr>
              <td></td>
              <td></td>

              <td>
                <InputGroup size="sm">
                  <Form.Select
                    value={filters.make ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilters((prev) => ({
                        ...prev,
                        make: v === "" ? null : Number(v),
                        // if make changes, clear model (since models are make-dependent in many systems)
                        model: null
                      }));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All</option>
                    {makes.map((m) => (
                      // FIX: use ID as value so URL + parsing stays consistent
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </td>

              <td></td>
              <td></td>
              <td></td>
              {!isMobileDevice() && <td></td>}
             
            </tr> */}
              </thead>

              <tbody>
                {vehicles.map((vehicle) => (
                  <tr
                    key={vehicle.id}
                    onClick={() => handleRowClick(vehicle.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{vehicle.stockNumber}</td>
                    <td>{vehicle.year}</td>
                    <td>{vehicle.makeName}</td>
                    <td>{vehicle.modelName}</td>
                    <td>{vehicle.trim}</td>
                    <td>{vehicle.color}</td>

                    {!isMobileDevice() && <td>{vehicle.vin}</td>}

                    {/* convert date object to a string */}
                    <td>{vehicle.created ? new Date(vehicle.created).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      + ', '
                      + new Date(vehicle.created).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                      : '—'}</td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-md-none">
            {vehicles.map(renderMobileCard)}
          </div>


        </div>

      )}

      {/* Pagination */}
      <nav className="mb-6">
        <ul className="pagination justify-content-end">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
              Previous
            </button>
          </li>

          {Array.from({ length: totalPages }, (_, index) => (
            <li
              key={index}
              className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                {index + 1}
              </button>
            </li>
          ))}

          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
              Next
            </button>
          </li>
        </ul>
      </nav>

      {/* Add Vehicle Modal */}
      <Modal
        show={showAddVehicleModal}
        onHide={closeAddVehicleModal}
        size="lg"
        scrollable
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>New Vehicle</Modal.Title>
        </Modal.Header>
        <NewVehicle barcode={barcode} />
      </Modal>
    </div>
  );
};

export default Vehicles;
